import {
  SettlementFundAoAEnum,
  SettlementFundMap,
} from '@interfaces/settlement-fund/settlement-fund.enum';
import { formatBalance } from '@shared/helpers/selected-accounts';
import {
  AANAccountType,
  AccountTypeNames,
  AccountTypes,
  CurrentInputAccount,
  Eligible_Joint_Account_Types_Pattern,
  Eligible_Trust_Account_Types,
  IReceipt,
  ISelectedAccount,
  IUserInputAccount,
  ProductType,
  TransactionTypesEnum,
} from '@shared/models';
import { ReviewContent } from '@shared/services/content/content-jsons/review-content';
import {
  InternalTransferState,
  InternalTransferTypeEnum,
} from '@store/reducers/internal-transfer/internal-transfer.reducer';
import { IOpenAccountState } from '@store/reducers/open-account/open-account.reducer';

import { toSentenceLowerCase } from '../cashier-nonretirement';

const accountTypesMap = new Map<string, string>([
  [AccountTypes.ESTATE, AccountTypeNames.ESTATE],
  [AccountTypes.EXECUTOR_ESTATE, AccountTypeNames.EXECUTOR_ESTATE],
  [AccountTypes.INDIVIDUAL, AccountTypeNames.INDV],
  [AccountTypes.INDV, AccountTypeNames.INDV],
  [AccountTypes.IRA, AccountTypeNames.IRA],
  [AccountTypes.ORG, AccountTypeNames.ORG],
  [AccountTypes.TRUST, AccountTypeNames.TRUST],
  [AccountTypes.OTHER, AccountTypeNames.OTHER],
]);

export const getAccountTypeName = (accountType: string | undefined): string => {
  if (!accountType) {
    return 'undefined';
  }
  if (Eligible_Joint_Account_Types_Pattern.test(accountType)) {
    return AccountTypeNames.JOINT;
  } else if ([...Eligible_Trust_Account_Types].map((x) => x.toString()).includes(accountType)) {
    return AccountTypeNames.TRUST;
  }
  return accountTypesMap.get(accountType) ?? AccountTypeNames.OTHER;
};

export const formatProductTypeName = (productType: ProductType | null) => {
  let formattedProductTypeName = '';
  if (productType) {
    formattedProductTypeName = productType
      .split('_')
      .map((word: string) => toSentenceLowerCase(word))
      .join(' ');
  }

  return formattedProductTypeName;
};

export const getAccountAndProductString = (
  isCashPlus: boolean,
  account: ISelectedAccount | null,
): string => {
  if (!account || !account.accountType) {
    return 'undefined account';
  }
  const accountTypeName = getAccountTypeName(account.accountType);
  const productTypeName = ![
    AccountTypes.ESTATE.toString(),
    AccountTypes.EXECUTOR_ESTATE.toString(),
  ].includes(account.accountType)
    ? ' ' +
      formatProductTypeName(isCashPlus ? ProductType.CASH_PLUS : account.accountData.productType)
    : '';
  return `${accountTypeName}${productTypeName} account`;
};

export function getTransferTypeAndMethodString(
  transferFrom: InternalTransferState['transferFrom'],
): string {
  return transferFrom.transferType === InternalTransferTypeEnum.ENTIRE_ACCOUNT
    ? toSentenceLowerCase(transferFrom.transferType)
    : toSentenceLowerCase(transferFrom.transferType) +
        ' in ' +
        toSentenceLowerCase(transferFrom.transferMethod).toLowerCase();
}

export function buildAccountFromInternalTransfer(
  transferFrom: InternalTransferState['transferFrom'],
  editable: boolean,
  hasAvgCostHoldings?: boolean,
): IReceipt {
  const isCashPlus: boolean = !!transferFrom.selectedAccount?.accountData?.cashManagementAccount;
  const receipt: IReceipt = {
    header: 'Transferring from',
    editable: editable,
    editRoute: 'secure/internal-transfer/transfer-from',
    sectionEvidenceID: 'accountFrom',
    rows: [
      {
        label: 'Account type',
        values: [getAccountAndProductString(isCashPlus, transferFrom.selectedAccount)],
        evidenceID: 'fromAccountType',
      },
      {
        label: 'Account number',
        values: [`${transferFrom.selectedAccount?.accountNumber}`],
        evidenceID: 'fromAccountNumber',
      },
      {
        label: 'Transfer method',
        values: [getTransferTypeAndMethodString(transferFrom)],
        evidenceID: 'transferMethod',
      },
      {
        label: 'Est. total transfer value',
        values: [formatBalance(transferFrom.totalTransferValue)],
        evidenceID: 'totalTransferValue',
        isLabelBold: true,
      },
    ],
  };
  if (!editable && hasAvgCostHoldings) {
    receipt.rows.push({
      label: 'Average cost change',
      values: [`${transferFrom.convertAvgCostHoldings ? 'Yes' : 'No'}`],
      evidenceID: 'hasAvgCostHoldings',
    });
  }

  if (transferFrom.selectedAccount?.accountData.nickname) {
    receipt.rows.splice(3, 0, {
      label: 'Account nickname',
      values: [transferFrom.selectedAccount.accountData.nickname],
      evidenceID: 'accountNickname',
    });
  }

  return receipt;
}

export function buildAccountToInternalTransfer(
  state: InternalTransferState,
  openAccount: IOpenAccountState,
  editable: boolean,
  settlementFund: SettlementFundAoAEnum | null,
  isVCDToggleOn: boolean,
): IReceipt {
  const triage: InternalTransferState['triage'] = state.triage;
  const transferTo: InternalTransferState['transferTo'] = state.transferTo;
  const isOwner: boolean = triage.isOwner;
  const toAccountNumber = transferTo.selectedAccount?.accountNumber;
  const isNewAccount: boolean =
    state.transactionType === TransactionTypesEnum.NEW_ACCOUNT ||
    state.transactionType === TransactionTypesEnum.FORCED_NEW_ACCOUNT;
  let isCashPlus: boolean = false;
  if (!isNewAccount) {
    // check if the account is not new and the user is the owner with a Cash Management Account.
    isCashPlus =
      isOwner &&
      !!(transferTo.selectedAccount as ISelectedAccount)?.accountData?.cashManagementAccount;
  }

  const receipt: IReceipt = {
    header: 'Transferring to',
    editable: editable,
    editRoute: isOwner
      ? 'secure/internal-transfer/transfer-to'
      : 'secure/internal-transfer/different-owner-recipient',
    sectionEvidenceID: 'accountTo',
    rows: [
      {
        label: 'Account type',
        values: [
          isSelectedAccountWithData(transferTo.selectedAccount)
            ? `${getAccountTypeName(transferTo.selectedAccount?.accountType)} ${formatProductTypeName(isCashPlus ? ProductType.CASH_PLUS : transferTo.selectedAccount?.accountData.productType)} account`
            : `${getAccountTypeName(triage.transferTo)} account`,
        ],
        evidenceID: 'toAccountType',
      },
      {
        label: 'Account number',
        values: [
          toAccountNumber === 'newAccount' ||
          !transferTo.selectedAccount?.accountNumber ||
          openAccount.openAccountResponse?.isS24Reject
            ? 'Account number pending'
            : transferTo.selectedAccount.accountNumber,
        ],
        evidenceID: 'toAccountNumber',
      },
    ],
  };

  if (!isOwner) {
    const toAccountName = (transferTo.selectedAccount as IUserInputAccount)?.accountName;
    const accountName = {
      label: 'Name of individual or trust',
      values: [toAccountName],
      evidenceID: 'toAccountName',
    };
    receipt.rows.splice(1, 0, accountName);
  }

  if (
    isVCDToggleOn &&
    (state.transactionType === TransactionTypesEnum.NEW_ACCOUNT ||
      state.transactionType === TransactionTypesEnum.FORCED_NEW_ACCOUNT)
  ) {
    const settlementFundLabel = {
      label: 'Settlement fund',
      values: [SettlementFundMap[settlementFund ?? SettlementFundAoAEnum.FED]].filter(
        (value): value is string => value !== undefined,
      ),
      evidenceID: 'settlementFund',
      additionalText: ReviewContent.settlementFundDescription,
    };
    receipt.rows.splice(2, 0, settlementFundLabel);
  }

  if (
    isSelectedAccountWithData(transferTo.selectedAccount) &&
    transferTo.selectedAccount.accountData.nickname
  ) {
    receipt.rows.push({
      label: 'Account nickname',
      values: [transferTo.selectedAccount.accountData.nickname],
      evidenceID: 'accountNickname',
    });
  }

  return receipt;
}

const isSelectedAccountWithData = (account: CurrentInputAccount): account is ISelectedAccount => {
  return !!(account as ISelectedAccount)?.accountData;
};
